import Head from "next/head";
import { useRouter } from "next/router";

import { useContext, useEffect, useMemo, useState } from "react";

import { twMerge } from "tailwind-merge";

import CustomCard from "components/CustomCard";
import PaneLayout from "components/PaneLayout";
import { Tab, Tabs } from "components/Tabs";
import { TopNavBarContext } from "components/TopNavBar/TopNavBarContext";
import useActivityTracker from "hooks/useActivityTracker";
import useMediaQuery from "hooks/useMediaQuery";
import { emberAppBaseUrl } from "utils/envVariables";

import CollectionsPageLayoutMobile from "./ColectionsPageLayoutMobile";
import CollectionsPageAll from "./CollectionsPageAll";
import CollectionsPageTabs, {
  type Tab as TabType,
  AVAILABLE_TABS,
} from "./CollectionsPageTabs";

const title = "Collections";
const description = "Curated groups of Insights from the Smartkarma network";

type CollectionsPageLayoutProps = {};

const CollectionsPageLayout = ({}: CollectionsPageLayoutProps) => {
  const { setShowBackButton } = useContext(TopNavBarContext);

  const media = useMediaQuery();
  const router = useRouter();
  const tab = router.query.tab as
    | "all"
    | "unpaywalled"
    | "recommended"
    | "trending"
    | "all-countries"
    | "all-sectors"
    | "all-content-verticals"
    | "top-countries"
    | "top-sectors"
    | "top-content-verticals"
    | "followed";

  const { trackActivity } = useActivityTracker();

  const canonicalUrl =
    emberAppBaseUrl + (router.asPath === "/" ? "" : router.asPath);
  const tabs =
    (AVAILABLE_TABS.find(
      (t) => t !== "separator" && t.segments.includes(tab)
    ) as TabType) || {};
  const [section, setSection] = useState(
    `collection-for-${tabs.pills?.[0].name}`
  );

  useEffect(() => {
    setShowBackButton(false);
  }, [setShowBackButton]);

  const children = useMemo(() => {
    if (!tab || tab === "all") {
      return (
        <CustomCard className="p-[15px] md:p-[30px]">
          <CollectionsPageAll type="generic" label="BROWSE ALL" />
        </CustomCard>
      );
    }

    if (!tabs.pills) return null;

    return tabs.pills.map((p) => (
      <div key={p.name} id={`collection-for-${p.name}`}>
        <CustomCard className="p-[15px] md:p-[30px]">
          <CollectionsPageAll
            type={p.segment as any}
            label={p.name}
            hidePills
          />
        </CustomCard>
      </div>
    ));
  }, [tab, tabs.pills]);

  const renderTabs = useMemo(() => {
    if (!tabs.pills) return null;

    if (tabs.name === "Unpaywalled") return null;

    return (
      <div className="mb-[20px] mt-0 md:mt-[-30px]">
        <Tabs className="p-0 m-0">
          {tabs.pills.map((pill) => {
            return (
              <Tab
                active={section === `collection-for-${pill.name}`}
                onClick={() => {
                  const topPos = document.getElementById(
                    `collection-for-${pill.name}`
                  )?.offsetTop;
                  document.documentElement.scrollTop = topPos || 0;
                  setSection(`collection-for-${pill.name}`);
                }}
                key={pill.name}
                className="md:!max-w-none"
              >
                {pill.name}
              </Tab>
            );
          })}
        </Tabs>
      </div>
    );
  }, [section, tabs.name, tabs.pills]);

  useEffect(() => {
    trackActivity("collection.list");
  }, [trackActivity]);

  useEffect(() => {
    setSection(`collection-for-${tabs.pills?.[0].name}`);
  }, [tab, tabs.pills]);

  useEffect(() => {
    const updateTabsPosition = () => {
      const pos = document.documentElement.scrollTop || document.body.scrollTop;

      if (pos === 0) {
        setSection(`collection-for-${tabs.pills?.[0].name}`);
      }
    };

    updateTabsPosition();
    window.addEventListener("scroll", updateTabsPosition, { passive: true });
    return () => {
      window.removeEventListener("scroll", updateTabsPosition);
    };
  }, [tabs.pills]);

  return (
    <>
      <Head>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="index, follow" />

        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />

        {/* Open graph tags for social media etc */}
        {/* <meta property="og:image" content={coverImageUrl} /> */}
        <meta property="og:image:width" content="200" />
        <meta property="og:image:height" content="200" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
      </Head>
      {media.mobile ? (
        <CollectionsPageLayoutMobile>
          {renderTabs}
          {children}
        </CollectionsPageLayoutMobile>
      ) : (
        <PaneLayout
          className="pl-[30px] py-[20px] min-h-[100vh]"
          left={<CollectionsPageTabs />}
          hideRight
          hideGoToTop
        >
          <div
            className={twMerge(
              "pl-[15px] mt-[45px]",
              "min-h-[100vh]",
              "w-full"
            )}
          >
            {renderTabs}
            {children}
          </div>
        </PaneLayout>
      )}
    </>
  );
};

export default CollectionsPageLayout;
